/**
 * API Client with Comprehensive Logging
 * Every API call is logged to CloudWatch with full context
 * Request + Response + Timing + Errors - ALL LOGGED
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { logger, LogLevel, LogCategory } from './logger.service';
import { getApiBaseUrl, isConfigLoaded } from '@/config/runtime-config';
import { authStorage } from '../lib/storage';
import tokenStorage from './token-storage';

/**
 * API Client Class
 */
class APIClient {
  private axios: AxiosInstance;
  private baseURL: string;

  constructor(baseURL: string = '') {
    this.baseURL = baseURL;

    this.axios = axios.create({
      baseURL,
      timeout: 30000,
      withCredentials: false, // JWT Bearer token auth (no cookies)
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Update base URL when config is loaded
   */
  public updateBaseURL(baseURL: string) {
    this.baseURL = baseURL;
    this.axios.defaults.baseURL = baseURL;
    logger.info('API Client base URL updated', { baseURL });
  }

  /**
   * Setup request and response interceptors with logging
   */
  private setupInterceptors() {
    // REQUEST INTERCEPTOR - Add Authorization header and log all outgoing requests
    this.axios.interceptors.request.use(
      config => {
        const requestId = uuidv4();

        // Add Cognito ID token as Bearer — backend Lambda authorizers decode the idToken
        // (not the accessToken) to extract user identity claims (sub, email, groups)
        const idToken = tokenStorage.getIdToken();

        if (idToken && !config.url?.includes('/auth/login') && !config.url?.includes('/auth/register')) {
          config.headers['Authorization'] = `Bearer ${idToken}`;
        }

        // Store start time for performance tracking
        (config as any).startTime = performance.now();
        (config as any).requestId = requestId;

        // Log request
        logger.log({
          level: LogLevel.DEBUG,
          category: LogCategory.API,
          message: `API Request: ${config.method?.toUpperCase()} ${config.url}`,
          requestId,
          data: {
            method: config.method?.toUpperCase(),
            url: config.url,
            fullUrl: `${this.baseURL}${config.url}`,
            endpoint: config.url,
            headers: this.sanitizeHeaders(config.headers),
            body: this.sanitizeBody(config.data),
            queryParams: config.params,
            hasAuthToken: !!idToken,
          },
        });

        return config;
      },
      error => {
        logger.error('API Request Setup Failed', error);
        return Promise.reject(error);
      }
    );

    // RESPONSE INTERCEPTOR - Log all responses (success & error)
    this.axios.interceptors.response.use(
      (response: AxiosResponse) => {
        const duration = performance.now() - (response.config as any).startTime;
        const requestId = (response.config as any).requestId;

        // Log successful response
        logger.log({
          level: LogLevel.INFO,
          category: LogCategory.API,
          message: `API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`,
          requestId,
          data: {
            request: {
              method: response.config.method?.toUpperCase() || 'GET',
              url: response.config.url || '',
              fullUrl: `${this.baseURL}${response.config.url}`,
              endpoint: response.config.url || '',
              headers: this.sanitizeHeaders(response.config.headers),
              body: this.sanitizeBody(response.config.data),
              queryParams: response.config.params,
            },
            response: {
              status: response.status,
              statusText: response.statusText,
              headers: this.sanitizeHeaders(response.headers),
              body: this.truncateResponseBody(response.data),
              timing: {
                duration: Math.round(duration),
                start: (response.config as any).startTime,
                end: performance.now(),
              },
            },
          },
        });

        return response;
      },
      async (error: AxiosError) => {
        const duration = error.config
          ? performance.now() - ((error.config as any).startTime || 0)
          : 0;
        const requestId = error.config ? (error.config as any).requestId : uuidv4();

        // Log error response
        logger.log({
          level: LogLevel.ERROR,
          category: LogCategory.API,
          message: `API Error: ${error.response?.status || 'Network Error'} ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
          requestId,
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack,
          },
          data: {
            request: error.config
              ? {
                  method: error.config.method?.toUpperCase() || 'GET',
                  url: error.config.url || '',
                  fullUrl: `${this.baseURL}${error.config.url}`,
                  endpoint: error.config.url || '',
                  headers: this.sanitizeHeaders(error.config.headers),
                  body: this.sanitizeBody(error.config.data),
                  queryParams: error.config.params,
                }
              : undefined,
            response: error.response
              ? {
                  status: error.response.status,
                  statusText: error.response.statusText,
                  headers: this.sanitizeHeaders(error.response.headers),
                  body: error.response.data,
                  timing: {
                    duration: Math.round(duration),
                    start: (error.config as any)?.startTime || 0,
                    end: performance.now(),
                  },
                }
              : undefined,
            errorDetails: {
              code: error.code,
              isCors: this.isCorsError(error),
              isTimeout: error.code === 'ECONNABORTED',
            },
          },
        });

        // Automatic token refresh on 401 Unauthorized
        if (error.response?.status === 401 && error.config) {
          const originalRequest = error.config as any;

          // Prevent infinite retry loop
          if (originalRequest._isRetry || originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/auth/login')) {
            return Promise.reject(error);
          }

          try {
            logger.log({
              level: LogLevel.INFO,
              category: LogCategory.AUTH,
              message: '401 detected - attempting automatic token refresh',
              requestId,
              data: {
                originalEndpoint: originalRequest.url,
                retryAttempt: true,
              },
            });

            // Mark request as retry to prevent infinite loop
            originalRequest._isRetry = true;

            // Import authService dynamically to avoid circular dependency
            const { default: authService } = await import('./auth.service');

            // Attempt to refresh the token
            await authService.refreshToken();

            logger.log({
              level: LogLevel.INFO,
              category: LogCategory.AUTH,
              message: 'Token refreshed successfully - retrying original request',
              requestId,
              data: {
                originalEndpoint: originalRequest.url,
              },
            });

            // Retry the original request with new token
            return this.axios.request(originalRequest);
          } catch (refreshError: any) {
            logger.log({
              level: LogLevel.ERROR,
              category: LogCategory.AUTH,
              message: 'Token refresh failed - user will be logged out',
              requestId,
              data: {
                refreshError: refreshError.message,
                originalEndpoint: originalRequest?.url,
              },
            });

            // Refresh failed - user needs to log in again
            // Clear tokens and user data
            tokenStorage.clearTokens();
            if (typeof localStorage !== 'undefined') {
              authStorage.remove('current_user');
            }

            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Sanitize headers - mask sensitive data
   */
  private sanitizeHeaders(headers: any): Record<string, string> {
    if (!headers) return {};

    const sanitized: Record<string, string> = {};

    Object.keys(headers).forEach(key => {
      const lowerKey = key.toLowerCase();

      if (lowerKey === 'authorization') {
        // Mask bearer token but show first few chars
        const value = String(headers[key]);
        if (value.startsWith('Bearer ')) {
          sanitized[key] = `Bearer ${value.substring(7, 20)}...`;
        } else {
          sanitized[key] = '[REDACTED]';
        }
      } else if (
        lowerKey === 'cookie' ||
        lowerKey === 'set-cookie' ||
        lowerKey.includes('api-key') ||
        lowerKey.includes('apikey') ||
        lowerKey.includes('x-api-key') ||
        lowerKey.includes('secret')
      ) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = String(headers[key]);
      }
    });

    return sanitized;
  }

  /**
   * Sanitize request body - recursively remove passwords and sensitive data
   */
  private sanitizeBody(body: any): any {
    if (!body) return body;
    if (typeof body !== 'object') return body;

    try {
      // Handle arrays
      if (Array.isArray(body)) {
        return body.map(item => this.sanitizeBody(item));
      }

      const sanitized: any = {};

      for (const key of Object.keys(body)) {
        const lowerKey = key.toLowerCase();
        const value = body[key];

        // Full redaction for highly sensitive fields
        if (
          lowerKey.includes('password') ||
          lowerKey.includes('secret') ||
          lowerKey.includes('apikey') ||
          lowerKey.includes('api_key') ||
          lowerKey.includes('privatekey') ||
          lowerKey.includes('private_key') ||
          lowerKey === 'ssn' ||
          lowerKey.includes('creditcard') ||
          lowerKey.includes('cardnumber') ||
          lowerKey === 'cvv' ||
          lowerKey === 'cvc'
        ) {
          sanitized[key] = '[REDACTED]';
          continue;
        }

        // Token redaction with partial visibility
        if (
          lowerKey.includes('token') &&
          typeof value === 'string' &&
          value.length > 20
        ) {
          sanitized[key] = `${value.substring(0, 10)}...[REDACTED]`;
          continue;
        }

        // Email masking
        if (
          lowerKey.includes('email') &&
          typeof value === 'string' &&
          value.includes('@')
        ) {
          const [localPart, domain] = value.split('@');
          if (localPart.length > 2) {
            sanitized[key] = `${localPart.substring(0, 2)}***@${domain}`;
          } else {
            sanitized[key] = `***@${domain}`;
          }
          continue;
        }

        // Phone number masking
        if (
          (lowerKey.includes('phone') || lowerKey.includes('mobile')) &&
          typeof value === 'string' &&
          value.length > 4
        ) {
          const lastFour = value.slice(-4);
          sanitized[key] = `***${lastFour}`;
          continue;
        }

        // Recursively sanitize nested objects
        if (typeof value === 'object' && value !== null) {
          sanitized[key] = this.sanitizeBody(value);
          continue;
        }

        // Keep non-sensitive data
        sanitized[key] = value;
      }

      return sanitized;
    } catch {
      return '[Cannot sanitize body]';
    }
  }

  /**
   * Truncate large response bodies
   */
  private truncateResponseBody(body: any): any {
    if (!body) return body;

    try {
      const str = JSON.stringify(body);
      if (str.length > 5000) {
        return `[TRUNCATED - ${str.length} bytes - ${JSON.stringify(body).substring(0, 100)}...]`;
      }
      return body;
    } catch {
      return '[Cannot stringify body]';
    }
  }

  /**
   * Check if error is CORS related
   */
  private isCorsError(error: AxiosError): boolean {
    return (
      error.message.includes('CORS') ||
      (error.message.includes('Network Error') && !error.response)
    );
  }

  // Public HTTP Methods

  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axios.get<T>(url, config);
    return response.data;
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axios.post<T>(url, data, config);
    return response.data;
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axios.put<T>(url, data, config);
    return response.data;
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axios.delete<T>(url, config);
    return response.data;
  }

  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axios.patch<T>(url, data, config);
    return response.data;
  }

  /**
   * Get the underlying axios instance for advanced use cases
   */
  public getAxiosInstance(): AxiosInstance {
    return this.axios;
  }
}

// Create singleton instance with placeholder URL
// Will be updated when runtime config loads
const apiClient = new APIClient('');

// Initialize with runtime config when available
if (isConfigLoaded()) {
  apiClient.updateBaseURL(getApiBaseUrl());
}

export default apiClient;
export { APIClient };
